/**
 * Unified Authentication Utilities
 * Single source of truth for token management across the entire platform
 */

import { abandonPreview } from './founderPreview';
import {
  OWNER_KEY, hasAccountData, purgeAccountData, stashGuestData, restoreGuestData, clearGuestPromptDismissal,
} from './accountDataPrivacy';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Single token key for the entire platform
const TOKEN_KEY = 'shs_token';

/**
 * Get the stored authentication token
 * @returns {string|null} The stored token or null if not present
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Store the authentication token
 * @param {string} token - The JWT token to store
 */
export const tokenUserId = (token) => {
  try { const p = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); return p.sub || p.user_id || p.id || null; } catch { return null; }
};

export const setToken = (token) => {
  if (token) {
    const uid = tokenUserId(token);
    const owner = localStorage.getItem(OWNER_KEY);
    // A different account must never inherit the previous account's data on this browser.
    if (uid && owner && owner !== uid) purgeAccountData();
    // Data present with no owner was created as a guest: set it aside until the learner confirms.
    else if (uid && !owner && hasAccountData()) stashGuestData();
    if (uid) localStorage.setItem(OWNER_KEY, uid);
    localStorage.setItem(TOKEN_KEY, token);
  }
};

// Ending a session removes the account's data from this browser and gives back any set-aside guest data.
const endAccountSession = () => {
  purgeAccountData();
  localStorage.removeItem(OWNER_KEY);
  clearGuestPromptDismissal();
  restoreGuestData();
};

/**
 * Clear the authentication token (logout)
 */
export const clearToken = () => {
  abandonPreview(localStorage.getItem(TOKEN_KEY));
  localStorage.removeItem(TOKEN_KEY);
  endAccountSession();
};

/**
 * Check if a token exists
 * @returns {boolean} True if token exists
 */
export const hasToken = () => {
  return !!getToken();
};

/**
 * Decode JWT token payload without verification
 * @param {string} token - The JWT token
 * @returns {object|null} The decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param {string} token - The JWT token (optional, uses stored token if not provided)
 * @returns {boolean} True if expired or invalid
 */
export const isTokenExpired = (token = null) => {
  const t = token || getToken();
  if (!t) return true;
  
  const payload = decodeToken(t);
  if (!payload || !payload.exp) return true;
  
  // Check if expiration time has passed (with 10 second buffer)
  return Date.now() >= (payload.exp * 1000 - 10000);
};

// Account data must not outlive its session or leak into another account (expiry, other-tab sign-in, stale tokens).
export const enforceAccountDataIsolation = () => {
  const owner = localStorage.getItem(OWNER_KEY);
  const token = getToken();
  const live = token && !isTokenExpired(token);
  const uid = live ? tokenUserId(token) : null;
  if (!owner) {
    // Session active from before owner-tagging: its data belongs to that account.
    if (uid) localStorage.setItem(OWNER_KEY, uid);
    return;
  }
  if (!live) {
    if (token) {
      // Expired session: sign out everywhere in the UI, same as an expired token found on load.
      clearToken();
      window.dispatchEvent(new CustomEvent('shs:auth-expired', { detail: { from: window.location.pathname + window.location.search, reason: 'expired' } }));
    } else endAccountSession();
    return;
  }
  if (uid && uid !== owner) { purgeAccountData(); localStorage.setItem(OWNER_KEY, uid); }
};

export const watchAccountDataIsolation = () => {
  const check = () => { if (document.visibilityState !== 'hidden') enforceAccountDataIsolation(); };
  const onStorage = (e) => { if (e.key === null || e.key === TOKEN_KEY) enforceAccountDataIsolation(); };
  enforceAccountDataIsolation();
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', check);
  window.addEventListener('storage', onStorage);
  const timer = window.setInterval(check, 60000);
  return () => {
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', check);
    window.removeEventListener('storage', onStorage);
    window.clearInterval(timer);
  };
};

/**
 * Authenticated fetch wrapper that automatically injects Authorization header
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export const authFetch = async (url, options = {}) => {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  // If unauthorized, clear token (it may be expired or invalid)
  if (response.status === 401) {
    const payload = await response.json().catch(() => ({}));
    // Only clear if it's actually an auth error, not just a missing token scenario
    if (token && (payload.detail === 'Could not validate credentials' || payload.detail === 'Token expired')) {
      clearToken();
      // P1 Fix 3 — Emit a global event so EducationAuthContext + protected
      // route watchers can null out user state and redirect to the canonical
      // login page with a safe return-path. Guarded for non-browser envs.
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        try {
          window.dispatchEvent(
            new CustomEvent('shs:auth-expired', {
              detail: {
                from:
                  typeof window.location !== 'undefined'
                    ? window.location.pathname + window.location.search
                    : '/',
                reason: payload.detail || 'unauthorized',
              },
            })
          );
        } catch (_e) { /* silent — event dispatch is best-effort */ }
      }
    }
  }
  
  return response;
};

/**
 * Helper for GET requests with auth
 * @param {string} url - The URL to fetch
 * @returns {Promise<Response>} The fetch response
 */
export const authGet = (url) => {
  return authFetch(url, { method: 'GET' });
};

/**
 * Helper for POST requests with auth
 * @param {string} url - The URL to fetch
 * @param {object} data - The data to send
 * @returns {Promise<Response>} The fetch response
 */
export const authPost = (url, data) => {
  return authFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Helper for PUT requests with auth
 * @param {string} url - The URL to fetch
 * @param {object} data - The data to send
 * @returns {Promise<Response>} The fetch response
 */
export const authPut = (url, data) => {
  return authFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Helper for DELETE requests with auth
 * @param {string} url - The URL to fetch
 * @returns {Promise<Response>} The fetch response
 */
export const authDelete = (url) => {
  return authFetch(url, { method: 'DELETE' });
};

/**
 * Migration helper: check for legacy tokens and migrate them
 * Call this once on app startup to handle users with old tokens
 */
export const migrateLegacyTokens = () => {
  const legacyKeys = ['education_token', 'clinic_token'];
  
  // If we already have the new token, don't migrate
  if (getToken()) return;
  
  // Try to find a valid legacy token and migrate it
  for (const key of legacyKeys) {
    const legacyToken = localStorage.getItem(key);
    if (legacyToken && !isTokenExpired(legacyToken)) {
      setToken(legacyToken);
      // Clean up legacy keys after migration
      legacyKeys.forEach(k => localStorage.removeItem(k));
      console.log(`Migrated token from ${key} to ${TOKEN_KEY}`);
      return;
    }
  }
  
  // Clean up any expired legacy tokens
  legacyKeys.forEach(k => localStorage.removeItem(k));
};

export default {
  getToken,
  setToken,
  clearToken,
  hasToken,
  decodeToken,
  isTokenExpired,
  authFetch,
  authGet,
  authPost,
  authPut,
  authDelete,
  migrateLegacyTokens,
  TOKEN_KEY,
};
